/**
 * Organization detail.
 */

import { useFetcher, useLoaderData } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Organization } from '@erp/contracts'
import type { Assignment, Note, Task, TimelineResult } from '@erp/contracts'
import { api, ApiError } from '../api'
import { handleSubjectIntent } from '../detail-actions'
import { useUser } from '../status-context'
import { Detail, RecordDetail } from '../components/record-detail'
import { AssigneePicker } from '../components/assignee-picker'
import { Button, PageHeader } from '../ui'
import { formatMoney } from '../format'

interface Data {
  organization: Organization
  timeline: TimelineResult
  notes: Note[]
  tasks: Task[]
  assignments: Assignment[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.organizationId
  if (!id) throw new Response('Missing organization id', { status: 400 })

  const [organization, timeline, notes, tasks, assignments] = await Promise.all([
    api.organizations.get(id),
    api.organizations.timeline(id, { limit: 50 }),
    api.notes.deletedFor('organization', id),
    api.tasks.forSubject('organization', id),
    api.assignments.forSubject('organization', id),
  ])
  return { organization, timeline, notes: notes.rows, tasks: tasks.rows, assignments }
}

export async function action({ request, params }: ActionFunctionArgs) {
  const id = params.organizationId
  if (!id) throw new Response('Missing organization id', { status: 400 })

  const form = await request.formData()
  const intent = String(form.get('intent'))

  const shared = await handleSubjectIntent(intent, form)
  if (shared) return shared

  try {
    if (intent === 'archive') {
      await api.organizations.archive(id)
      return { ok: true }
    }
    return { ok: false, message: 'Unknown action' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, issues: error.issues }
    throw error
  }
}

export default function OrganizationDetail() {
  const { organization, timeline, notes, tasks, assignments } = useLoaderData() as Data
  const fetcher = useFetcher()
  const owner = useUser(organization.ownerId)

  return (
    <>
      <PageHeader
        title={organization.name}
        actions={
          <>
            <a className="ax-btn" href={`/crm/organizations/${organization.id}/edit`}>
              Edit
            </a>
            {organization.archivedAt ? (
              <span className="ax-text-muted">Archived</span>
            ) : (
              <fetcher.Form method="post">
                <input type="hidden" name="intent" value="archive" />
                <Button type="submit" disabled={fetcher.state !== 'idle'}>
                  Archive
                </Button>
              </fetcher.Form>
            )}
          </>
        }
      />

      <RecordDetail
        subject={{ type: 'organization', id: organization.id }}
        timeline={timeline}
        notes={notes}
        tasks={tasks}
        side={
          <>
            <AssigneePicker subject={{ type: 'organization', id: organization.id }} assignments={assignments} />
            <Detail label="Owner" value={owner?.name ?? null} />
            <Detail label="Website" value={organization.website} />
            <Detail label="Employees" value={organization.noOfEmployees} />
            <Detail
              label="Annual revenue"
              value={
                organization.annualRevenue !== null
                  ? formatMoney(organization.annualRevenue, organization.currency)
                  : null
              }
            />
            <Detail label="City" value={organization.city} />
            <Detail label="Country" value={organization.country} />
          </>
        }
      />
    </>
  )
}

